import { useCallback, useEffect, useState } from "react";
import type { FilterState } from "./useSkillCheckFilters";

/**
 * フィルタプリセットの型定義
 */
export type FilterPreset = {
  id: string;
  name: string;
  filters: Omit<FilterState, "page">;
  createdAt: string;
};

const STORAGE_KEY = "skill-check-filter-presets";
const MAX_PRESETS = 10;

/**
 * localStorage からプリセットを読み込む
 */
function loadPresets(): FilterPreset[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn("Failed to load filter presets:", error);
  }
  return [];
}

/**
 * localStorage にプリセットを保存する
 */
function savePresets(presets: FilterPreset[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch (error) {
    console.warn("Failed to save filter presets:", error);
  }
}

/**
 * ユニークなIDを生成
 */
function generateId(): string {
  return `preset-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * フィルタプリセットを管理するカスタムフック
 *
 * 機能:
 * - プリセットの保存/削除/読み込み
 * - localStorage への永続化
 * - 最大10件までのプリセット管理
 */
export function useFilterPresets() {
  const [presets, setPresets] = useState<FilterPreset[]>([]);

  // 初期化時にlocalStorageから読み込み
  useEffect(() => {
    setPresets(loadPresets());
  }, []);

  /**
   * 新しいプリセットを保存
   */
  const savePreset = useCallback(
    (name: string, filters: Omit<FilterState, "page">): boolean => {
      // 最大件数チェック
      if (presets.length >= MAX_PRESETS) {
        return false;
      }

      const newPreset: FilterPreset = {
        id: generateId(),
        name,
        filters,
        createdAt: new Date().toISOString(),
      };

      const updatedPresets = [newPreset, ...presets];
      setPresets(updatedPresets);
      savePresets(updatedPresets);
      return true;
    },
    [presets],
  );

  /**
   * プリセットを削除
   */
  const deletePreset = useCallback(
    (id: string): void => {
      const updatedPresets = presets.filter((p) => p.id !== id);
      setPresets(updatedPresets);
      savePresets(updatedPresets);
    },
    [presets],
  );

  /**
   * 全プリセットをクリア
   */
  const clearAllPresets = useCallback(() => {
    setPresets([]);
    savePresets([]);
  }, []);

  /**
   * プリセット名を更新
   */
  const renamePreset = useCallback(
    (id: string, newName: string): void => {
      const updatedPresets = presets.map((p) => (p.id === id ? { ...p, name: newName } : p));
      setPresets(updatedPresets);
      savePresets(updatedPresets);
    },
    [presets],
  );

  return {
    presets,
    savePreset,
    deletePreset,
    clearAllPresets,
    renamePreset,
    canSaveMore: presets.length < MAX_PRESETS,
    maxPresets: MAX_PRESETS,
  };
}
